<script setup lang="ts">
/** Détail d'une demande d'accompagnement — brief, délais, timeline et actions. */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'
import {
  ArrowLeftIcon,
  UserPlusIcon,
  HandRaisedIcon,
  HandThumbDownIcon,
  BellAlertIcon,
  ClockIcon,
  BoltIcon,
  PlayIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
} from '@heroicons/vue/24/outline'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import {
  canAssign,
  canCancel,
  canClose,
  canComment,
  canEdit,
  canRefuse,
  canRemind,
  canSelfTake,
  canTakeCharge,
} from '@/v2/accompagnement/helpers/accompagnementPermissions'
import {
  ACCOMP_DOCUMENT_LABEL,
  ACCOMP_SCOPE_LABEL,
  ACCOMP_TYPE_LABEL,
} from '@/v2/accompagnement/constants/accompagnementConstants'
import { formatDateShort, formatDateTime } from '@/v2/accompagnement/helpers/accompagnementFormat'
import { isTakeChargeOverdue } from '@/v2/accompagnement/helpers/accompagnementDelays'
import { formatAmount } from '@/v2/components/commercial-dashboard/dashboardFormat'
import AccompStatusBadge from '@/v2/accompagnement/components/AccompStatusBadge.vue'
import AccompUrgencyBadge from '@/v2/accompagnement/components/AccompUrgencyBadge.vue'
import AccompStatsCards from '@/v2/accompagnement/components/AccompStatsCards.vue'
import AccompTimeline from '@/v2/accompagnement/components/AccompTimeline.vue'
import AccompActionsSection from '@/v2/accompagnement/components/AccompActionsSection.vue'
import AccompQuotesSection from '@/v2/accompagnement/components/AccompQuotesSection.vue'
import AccompAssignDialog from '@/v2/accompagnement/components/AccompAssignDialog.vue'
import AccompNoteDialog from '@/v2/accompagnement/components/AccompNoteDialog.vue'
import AccompResolveDialog from '@/v2/accompagnement/components/AccompResolveDialog.vue'

const route = useRoute()
const router = useRouter()
const store = useAccompagnementStore()
const perms = useBoostCorePermissionStore()
const toast = useToast()

const { current, currentLoading, currentError, actionLoading, currentUserUuid } = storeToRefs(store)

const id = computed(() => route.params.id as string)

const assignTarget = ref<typeof current.value>(null)
const closeOpen = ref(false)
const cancelOpen = ref(false)
const refuseOpen = ref(false)
const remindOpen = ref(false)
const newComment = ref('')

const can = computed(() => {
  const req = current.value
  if (!req) {
    return {
      assign: false,
      selfTake: false,
      selfStart: false,
      takeCharge: false,
      refuse: false,
      remind: false,
      edit: false,
      close: false,
      cancel: false,
      comment: false,
    }
  }
  const uid = currentUserUuid.value
  return {
    assign: canAssign(req, perms, uid),
    selfTake: canSelfTake(req, perms) && !perms.can('ACCOMP_ADMIN'),
    selfStart: perms.can('ACCOMP_ADMIN') && (req.status === 'NOUVELLE' || req.status === 'ATTRIBUEE'),
    takeCharge: canTakeCharge(req, perms, uid),
    refuse: canRefuse(req, perms, uid),
    remind: canRemind(req, perms),
    edit: canEdit(req, perms, uid),
    close: canClose(req, perms, uid),
    cancel: canCancel(req, perms, uid),
    comment: canComment(perms),
  }
})

const overdue = computed(() => (current.value ? isTakeChargeOverdue(current.value) : false))
const refusedCount = computed(
  () => current.value?.statusHistory.filter((e) => e.type === 'REFUSED').length ?? 0,
)
/** Clôture conditionnée à au moins une action chronométrée (≥ 1 min). */
const hasTimedAction = computed(
  () => current.value?.actions.some((a) => (a.timeSpentMinutes ?? 0) >= 1) ?? false,
)

const scopeLabels = computed(() => current.value?.scope.map((s) => ACCOMP_SCOPE_LABEL[s]) ?? [])
const documentLabels = computed(
  () => current.value?.availableDocuments.map((d) => ACCOMP_DOCUMENT_LABEL[d]) ?? [],
)

async function reload() {
  await store.fetchOne(id.value)
}

async function onAssignConfirmed(payload: { technicianUuid: string; note?: string }) {
  if (!current.value) return
  const res = await store.assign(current.value.id, payload.technicianUuid, payload.note)
  if (res) {
    toast.success('Demande attribuée.')
    assignTarget.value = null
  } else {
    toast.error(store.actionError ?? "Échec de l'attribution.")
  }
}

async function selfTake() {
  if (!current.value) return
  const res = await store.assign(current.value.id, currentUserUuid.value)
  if (res) toast.success('Demande prise.')
  else toast.error(store.actionError ?? 'Action impossible.')
}

async function takeCharge() {
  if (!current.value) return
  const res = await store.takeCharge(current.value.id)
  if (res) toast.success('Prise en charge enregistrée.')
  else toast.error(store.actionError ?? 'Action impossible.')
}

async function selfAssignAndStart() {
  if (!current.value) return
  const res = await store.selfAssignAndStart(current.value.id)
  if (res) toast.success('Demande prise et démarrée.')
  else toast.error(store.actionError ?? 'Action impossible.')
}

function goEdit() {
  if (current.value) router.push(`/accompagnement/${current.value.id}/editer`)
}

async function onResolve(payload: { outcome: 'GAGNEE' | 'PERDUE'; note: string }) {
  if (!current.value) return
  const res = await store.close(current.value.id, payload.outcome, payload.note || undefined)
  if (res) {
    toast.success(payload.outcome === 'GAGNEE' ? 'Demande clôturée (gagnée).' : 'Demande clôturée (perdue).')
    closeOpen.value = false
  } else {
    toast.error(store.actionError ?? 'Action impossible.')
  }
}

async function onCancel(note: string) {
  if (!current.value) return
  const res = await store.cancel(current.value.id, note)
  if (res) {
    toast.success('Demande annulée.')
    cancelOpen.value = false
  } else {
    toast.error(store.actionError ?? 'Action impossible.')
  }
}

async function onRefuse(note: string) {
  if (!current.value) return
  const res = await store.refuse(current.value.id, note)
  if (res) {
    toast.success('Demande refusée — renvoyée au dispatcher.')
    refuseOpen.value = false
  } else {
    toast.error(store.actionError ?? 'Action impossible.')
  }
}

async function onRemind(note: string) {
  if (!current.value) return
  const res = await store.remind(current.value.id, note)
  if (res) {
    toast.success('Relance enregistrée.')
    remindOpen.value = false
  } else {
    toast.error(store.actionError ?? 'Action impossible.')
  }
}

async function submitComment() {
  if (!current.value || !newComment.value.trim()) return
  const res = await store.addComment(current.value.id, newComment.value.trim())
  if (res) newComment.value = ''
  else toast.error(store.actionError ?? "Échec de l'ajout du commentaire.")
}

onMounted(reload)
watch(id, reload)
</script>

<template>
  <div class="mx-auto w-full max-w-6xl p-4 md:p-8">
    <RouterLink to="/accompagnement" class="mb-3 inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-primary">
      <ArrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      Retour aux demandes
    </RouterLink>

    <!-- Chargement / erreur -->
    <div v-if="currentLoading" class="py-16 text-center">
      <span class="loading loading-spinner" aria-hidden="true"></span>
      <p class="mt-2 text-base-content/70">Chargement de la demande…</p>
    </div>
    <div v-else-if="currentError || !current" role="alert" class="alert alert-error">
      <span>Demande introuvable.</span>
      <RouterLink to="/accompagnement" class="btn btn-sm">Retour</RouterLink>
    </div>

    <template v-else>
      <!-- En-tête -->
      <header class="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="text-2xl font-bold tabular-nums">{{ current.reference }}</h1>
            <AccompStatusBadge :status="current.status" />
            <AccompUrgencyBadge :urgency="current.urgency" />
            <span v-if="overdue" class="badge badge-error gap-1">
              <ClockIcon class="h-3.5 w-3.5" aria-hidden="true" />
              En retard
            </span>
            <span v-if="refusedCount > 0" class="badge badge-warning gap-1">
              <HandThumbDownIcon class="h-3.5 w-3.5" aria-hidden="true" />
              Refusée {{ refusedCount }}×
            </span>
          </div>
          <p class="mt-1 text-base-content/80">
            {{ current.client.name }}
            <span v-if="current.client.siren" class="text-base-content/60"> · SIREN {{ current.client.siren }}</span>
            <span v-if="current.client.naf" class="text-base-content/60"> · NAF {{ current.client.naf }}</span>
          </p>
        </div>
      </header>

      <!-- Barre d'actions -->
      <div class="mb-5 flex flex-wrap gap-2">
        <button v-if="can.selfTake" type="button" class="btn btn-primary gap-1" :disabled="actionLoading" @click="selfTake">
          <HandRaisedIcon class="h-5 w-5" aria-hidden="true" />
          Prendre cette demande
        </button>
        <button v-if="can.assign" type="button" class="btn btn-outline gap-1" @click="assignTarget = current">
          <UserPlusIcon class="h-5 w-5" aria-hidden="true" />
          {{ current.assignedToUuid ? 'Réaffecter' : 'Attribuer' }}
        </button>
        <button v-if="can.selfStart" type="button" class="btn btn-primary gap-1" :disabled="actionLoading" @click="selfAssignAndStart">
          <PlayIcon class="h-5 w-5" aria-hidden="true" />
          Me l'attribuer et démarrer
        </button>
        <button v-if="can.takeCharge" type="button" class="btn btn-primary gap-1" :disabled="actionLoading" @click="takeCharge">
          <BoltIcon class="h-5 w-5" aria-hidden="true" />
          Prendre en charge
        </button>
        <button v-if="can.refuse" type="button" class="btn btn-outline btn-error gap-1" :disabled="actionLoading" @click="refuseOpen = true">
          <HandThumbDownIcon class="h-5 w-5" aria-hidden="true" />
          Refuser
        </button>
        <button v-if="can.remind" type="button" class="btn btn-ghost gap-1" :disabled="actionLoading" @click="remindOpen = true">
          <BellAlertIcon class="h-5 w-5" aria-hidden="true" />
          Relancer
        </button>
        <button v-if="can.edit" type="button" class="btn btn-outline gap-1" @click="goEdit">
          <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
          Éditer
        </button>
        <button
          v-if="can.close"
          type="button"
          class="btn btn-outline gap-1"
          :disabled="!hasTimedAction"
          :title="hasTimedAction ? undefined : 'Ajoutez une action avec temps passé avant de clôturer'"
          @click="closeOpen = true"
        >
          <CheckCircleIcon class="h-5 w-5" aria-hidden="true" />
          Clôturer
        </button>
        <button v-if="can.cancel" type="button" class="btn btn-ghost gap-1 text-error" @click="cancelOpen = true">
          <XCircleIcon class="h-5 w-5" aria-hidden="true" />
          Annuler
        </button>
      </div>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <!-- Colonne principale : le brief -->
        <div class="space-y-5 lg:col-span-2">
          <section class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body gap-4 p-4 md:p-6">
              <h2 class="card-title text-lg font-semibold">Demande</h2>
              <dl class="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div>
                  <dt class="text-sm text-base-content/70">Type de demande</dt>
                  <dd class="font-medium">{{ ACCOMP_TYPE_LABEL[current.requestType] }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Temps estimé</dt>
                  <dd class="font-medium tabular-nums">
                    {{ current.estimatedHours != null ? current.estimatedHours + ' h' : '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Demandeur</dt>
                  <dd class="font-medium">{{ current.createdByName }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Assigné à</dt>
                  <dd class="font-medium">{{ current.assignedToName ?? 'Non attribuée' }}</dd>
                </div>
              </dl>
            </div>
          </section>

          <section class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body gap-2 p-4 md:p-6">
              <h2 class="card-title text-lg font-semibold">Objectif du client</h2>
              <p class="whitespace-pre-line text-base-content/80">{{ current.objective || '—' }}</p>
            </div>
          </section>

          <section class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body gap-4 p-4 md:p-6">
              <h2 class="card-title text-lg font-semibold">Contexte</h2>
              <dl class="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div>
                  <dt class="text-sm text-base-content/70">Activité</dt>
                  <dd class="font-medium">{{ current.context.activity || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Sites / Collaborateurs</dt>
                  <dd class="font-medium tabular-nums">
                    {{ current.context.sites ?? '—' }} site(s) · {{ current.context.collaborators ?? '—' }} pers.
                  </dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Télétravail</dt>
                  <dd class="font-medium">{{ current.context.remoteWork || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Croissance prévue</dt>
                  <dd class="font-medium">{{ current.context.growth || '—' }}</dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="text-sm text-base-content/70">Contraintes particulières</dt>
                  <dd class="whitespace-pre-line text-base-content/80">{{ current.context.constraints || '—' }}</dd>
                </div>
              </dl>
            </div>
          </section>

          <section class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body gap-4 p-4 md:p-6">
              <h2 class="card-title text-lg font-semibold">Périmètre & documents</h2>
              <div>
                <p class="mb-1 text-sm text-base-content/70">Sujets concernés</p>
                <div v-if="scopeLabels.length" class="flex flex-wrap gap-1.5">
                  <span v-for="s in scopeLabels" :key="s" class="badge badge-outline">{{ s }}</span>
                </div>
                <p v-else class="text-base-content/60">—</p>
              </div>
              <div>
                <p class="mb-1 text-sm text-base-content/70">Documents disponibles</p>
                <div v-if="documentLabels.length" class="flex flex-wrap gap-1.5">
                  <span v-for="d in documentLabels" :key="d" class="badge badge-outline">{{ d }}</span>
                </div>
                <p v-else class="text-base-content/60">—</p>
              </div>
            </div>
          </section>

          <section class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body gap-4 p-4 md:p-6">
              <h2 class="card-title text-lg font-semibold">Contraintes projet</h2>
              <dl class="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div>
                  <dt class="text-sm text-base-content/70">Date souhaitée</dt>
                  <dd class="font-medium tabular-nums">{{ formatDateShort(current.projectConstraints.desiredDate) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Date impérative</dt>
                  <dd class="font-medium tabular-nums">{{ formatDateShort(current.projectConstraints.mandatoryDate) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Décisionnaire</dt>
                  <dd class="font-medium">{{ current.projectConstraints.decisionMaker || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Concurrents</dt>
                  <dd class="font-medium tabular-nums">{{ current.projectConstraints.competitorCount ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Budget connu</dt>
                  <dd class="font-medium">
                    <template v-if="current.projectConstraints.budgetKnown">
                      Oui<template v-if="current.projectConstraints.budgetAmount != null">
                        · <span class="tabular-nums">{{ formatAmount(current.projectConstraints.budgetAmount) }}</span></template>
                    </template>
                    <template v-else>Non</template>
                  </dd>
                </div>
                <div>
                  <dt class="text-sm text-base-content/70">Appel d'offres</dt>
                  <dd class="font-medium">{{ current.projectConstraints.tender ? 'Oui' : 'Non' }}</dd>
                </div>
              </dl>
            </div>
          </section>

          <AccompActionsSection :request="current" />

          <AccompQuotesSection :request="current" />
        </div>

        <!-- Colonne latérale : délais + timeline + commentaires -->
        <div class="space-y-5">
          <!-- Indicateurs de pilotage (délais + temps) : heures ouvrées → réservés
               WRITE/ADMIN. Sans intérêt pour le demandeur (commercial / READ). -->
          <AccompStatsCards v-if="perms.can('ACCOMP_WRITE')" :request="current" />

          <section class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body gap-3 p-4 md:p-6">
              <h2 class="card-title text-lg font-semibold">Suivi</h2>
              <p class="text-sm text-base-content/70">
                Créée le <span class="tabular-nums">{{ formatDateTime(current.createdAt) }}</span>
              </p>
              <AccompTimeline :events="current.statusHistory" />

              <div v-if="can.comment" class="mt-1 border-t border-base-200 pt-3">
                <label for="new-comment" class="mb-1 block text-sm font-medium text-base-content">
                  Ajouter un commentaire
                </label>
                <textarea
                  id="new-comment"
                  v-model="newComment"
                  rows="2"
                  class="textarea textarea-bordered w-full text-base"
                  placeholder="Votre message…"
                ></textarea>
                <div class="mt-2 flex justify-end">
                  <button
                    type="button"
                    class="btn btn-primary btn-sm gap-1"
                    :disabled="!newComment.trim() || actionLoading"
                    @click="submitComment"
                  >
                    <PaperAirplaneIcon class="h-4 w-4" aria-hidden="true" />
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <AccompAssignDialog
        :request="assignTarget"
        :loading="actionLoading"
        @close="assignTarget = null"
        @confirmed="onAssignConfirmed"
      />
      <AccompResolveDialog
        :open="closeOpen"
        :loading="actionLoading"
        :can-win="(current.quotes?.length ?? 0) > 0"
        @close="closeOpen = false"
        @confirmed="onResolve"
      />
      <AccompNoteDialog
        :open="cancelOpen"
        title="Annuler la demande"
        label="Motif d'annulation"
        confirm-label="Annuler la demande"
        variant="error"
        required
        @close="cancelOpen = false"
        @confirmed="onCancel"
      />
      <AccompNoteDialog
        :open="refuseOpen"
        title="Refuser la demande"
        label="Motif du refus (transmis au dispatcher)"
        confirm-label="Refuser"
        variant="error"
        required
        @close="refuseOpen = false"
        @confirmed="onRefuse"
      />
      <AccompNoteDialog
        :open="remindOpen"
        title="Relancer le technicien"
        label="Message (facultatif)"
        confirm-label="Relancer"
        @close="remindOpen = false"
        @confirmed="onRemind"
      />
    </template>
  </div>
</template>
